import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import KpiCard from './KpiCard';
import { useDashboardQuery } from '../../hooks/queries/useDashboardQuery';
import './KpiCardsGroup.css';

const getSalesforceIdFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId') || 'a0JPC00000cKHlB2AW';
};

const calculateDaysDifference = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

interface KpiMetrics {
  gross_sales: string | number;
  total_refunds: string | number;
  net_sales: string | number;
  order_count: string | number;
  avg_ticket: string | number;
  avg_processing_time: string | number;
}

interface DashboardMetrics {
  total: KpiMetrics;
  dineIn: KpiMetrics;
  nonDineIn: KpiMetrics;
}

const formatCurrency = (value: string | number): string => {
  if (typeof value === 'string' && value.startsWith('$')) {
    return value;
  }
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
  return `$${Number(numValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatNumber = (value: string | number): string => {
  if (typeof value === 'string' && value.includes(',')) {
    return value;
  }
  return Number(value).toLocaleString('en-US');
};

const calculateDailyAverage = (totalValue: string | number, days: number): number => {
  const numValue = typeof totalValue === 'string' ? 
    parseFloat(totalValue.replace(/[$,]/g, '')) : 
    Number(totalValue);
  return numValue / days;
};

const KpiCardsGroup: React.FC = () => {
  const { t } = useTranslation();
  const { 
    isFiltersApplying,
    dateRange,
    dayOfWeek,
    time,
    diningBehavior,
    isLoadingDefaultDates 
  } = useFilters();
  
  const salesforceId = getSalesforceIdFromUrl();

  const formatTime = (seconds: string | number): string => {
    if (typeof seconds === 'string' && (seconds.includes('시간') || seconds.includes('분') || seconds.includes('초') || seconds.toLowerCase().includes('h'))) {
      return seconds;
    }
    const numSeconds = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
    const hours = Math.floor(numSeconds / 3600);
    const minutes = Math.floor((numSeconds % 3600) / 60);
    const secs = Math.floor(numSeconds % 60);
    let result = '';
    if (hours > 0) result += t('hours', { count: hours }) + ' ';
    if (minutes > 0 || hours > 0) result += t('minutes', { count: minutes }) + ' ';
    result += t('seconds', { count: secs });
    return result.trim();
  };

  
  const { 
    data: dashboardData, 
    isLoading: loading, 
    error: queryError 
  } = useDashboardQuery({
    storeId: salesforceId,
    filters: {
      dateRange,
      dayOfWeek,
      time,
      diningBehavior
    },
    enabled: !isLoadingDefaultDates && !!salesforceId
  });
  
  const error = queryError ? 
    (queryError instanceof Error ? queryError.message : t('errorDataLoad')) : 
    null;
  
  
  const metricsData = React.useMemo((): DashboardMetrics | null => {
    if (!dashboardData?.cardMetrics) return null;
    
    const days = calculateDaysDifference(dateRange.startDate, dateRange.endDate);
    
    return {
      total: {
        ...dashboardData.cardMetrics.total,
        net_sales: calculateDailyAverage(dashboardData.cardMetrics.total.net_sales, days),
        order_count: calculateDailyAverage(dashboardData.cardMetrics.total.order_count, days)
      },
      dineIn: {
        ...dashboardData.cardMetrics.dineIn,
        net_sales: calculateDailyAverage(dashboardData.cardMetrics.dineIn.net_sales, days),
        order_count: calculateDailyAverage(dashboardData.cardMetrics.dineIn.order_count, days)
      },
      nonDineIn: {
        ...dashboardData.cardMetrics.nonDineIn,
        net_sales: calculateDailyAverage(dashboardData.cardMetrics.nonDineIn.net_sales, days),
        order_count: calculateDailyAverage(dashboardData.cardMetrics.nonDineIn.order_count, days)
      }
    };
  }, [dashboardData, dateRange]);

  if (error) {
    return (
      <div className="kpi-cards-group error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="kpi-cards-group">
      {}
      {}
      
      <KpiCard 
        title={t('kpiCardsGroup.dailyAverageSales')} 
        icon="💰"
        metricKey="net_sales"
        totalData={metricsData?.total}
        dineInData={metricsData?.dineIn}
        nonDineInData={metricsData?.nonDineIn}
        formatFn={formatCurrency}
        isLoading={loading || isFiltersApplying}
        color="#474646"
        accentColor="#F97F76"
        showPercentage={true}
      />
      
      <KpiCard 
        title={t('kpiCardsGroup.dailyAverageOrders')} 
        icon="📋"
        metricKey="order_count"
        totalData={metricsData?.total}
        dineInData={metricsData?.dineIn}
        nonDineInData={metricsData?.nonDineIn}
        formatFn={(value) => formatNumber(Math.round(Number(value)))}
        isLoading={loading || isFiltersApplying}
        color="#474646"
        accentColor="#F97F76"
        showPercentage={true}
      />
      
      <KpiCard 
        title={t('kpiCardsGroup.averageTicketSales')} 
        icon="💳"
        metricKey="avg_ticket"
        totalData={metricsData?.total}
        dineInData={metricsData?.dineIn}
        nonDineInData={metricsData?.nonDineIn}
        formatFn={formatCurrency}
        isLoading={loading || isFiltersApplying}
        color="#474646"
        accentColor="#F97F76"
        showPercentage={false}
      />
      
      <KpiCard 
        title={t('kpiCardsGroup.orderToPaymentTime')} 
        icon="⏱️"
        metricKey="avg_processing_time"
        totalData={metricsData?.total}
        dineInData={metricsData?.dineIn}
        nonDineInData={metricsData?.nonDineIn}
        formatFn={(value) => formatTime(value)}
        isLoading={loading || isFiltersApplying}
        color="#474646"
        accentColor="#F97F76"
        showPercentage={false}
      />
    </div>
  );
};

export default KpiCardsGroup;